import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { signupApi } from "apis/eduidSignup";
import { CustomInput } from "components/Common/CustomInput";
import { EduIDButton } from "components/Common/EduIDButton";
import { EmailInput } from "components/Common/EmailInput";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { validateSignupUserInForm } from "helperFunctions/validation/validateEmail";
import { useEffect } from "react";
import { Field as FinalField, Form as FinalForm } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
import { clearNotifications } from "slices/Notifications";
import { signupSlice } from "slices/Signup";
import { ServiceInfo } from "./SignupInfo";
import { SignupStepIndicator } from "./SignupStepIndicator";
export function SignupEmailForm() {
    return (_jsxs("div", { className: "step-container", children: [_jsxs("section", { className: "intro", children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "emailForm.create", defaultMessage: "Create eduID: Enter your personal information", description: "Signup" }) }), _jsx(ServiceInfo, {}), _jsx("div", { className: "lead", children: _jsx("p", { children: _jsx(FormattedMessage, { id: "emailForm.text", defaultMessage: `Make sure to use an email address you have access to, as it will need to be confirmed by a received code. `, description: "Signup email form text" }) }) })] }), _jsx(EmailForm, {}), _jsx(SignupStepIndicator, { currentStep: 1 })] }));
}
/* FORM */
export function EmailForm() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const dispatch = useAppDispatch();
    const state = useAppSelector((state) => state.signup.state);
    const intl = useIntl();
    if (!state)
        return null;
    const givenName = (_d = (_b = (_a = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _a === void 0 ? void 0 : _a.given_name) !== null && _b !== void 0 ? _b : (_c = state === null || state === void 0 ? void 0 : state.name) === null || _c === void 0 ? void 0 : _c.given_name) !== null && _d !== void 0 ? _d : "";
    const surname = (_h = (_f = (_e = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _e === void 0 ? void 0 : _e.surname) !== null && _f !== void 0 ? _f : (_g = state === null || state === void 0 ? void 0 : state.name) === null || _g === void 0 ? void 0 : _g.surname) !== null && _h !== void 0 ? _h : "";
    const firstNamePlaceholder = intl.formatMessage({
        id: "emailForm.placeholder",
        defaultMessage: "first name",
        description: "placeholder First name",
    });
    const lastNamePlaceholder = intl.formatMessage({
        id: "emailForm.lastPlaceholder",
        defaultMessage: "last name",
        description: "placeholder Last name",
    });
    function submitEmailForm(values) {
        var _a, _b, _c, _d, _e, _f, _g;
        const errors = {};
        if (values) {
            const signupUser = {
                email: (_a = values.email) !== null && _a !== void 0 ? _a : "",
                given_name: (_c = (_b = values.given_name) === null || _b === void 0 ? void 0 : _b.replace(/^\w/, (c) => c.toUpperCase())) !== null && _c !== void 0 ? _c : "",
                surname: (_e = (_d = values.surname) === null || _d === void 0 ? void 0 : _d.replace(/^\w/, (c) => c.toUpperCase())) !== null && _e !== void 0 ? _e : "",
            };
            dispatch(signupSlice.actions.setEmail(signupUser));
            // We ask for the e-mail address first, but we don't pass it to the backend until the user has accepted the ToU
            // terms of use, and solved a captcha. So we store it in the redux state here.
            if (((state === null || state === void 0 ? void 0 : state.captcha.completed) && (state === null || state === void 0 ? void 0 : state.tou.completed)) ||
                (((_f = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _f === void 0 ? void 0 : _f.completed) && (state === null || state === void 0 ? void 0 : state.tou.completed))) {
                // Go to RegisterEmail
                dispatch(signupSlice.actions.setNextPage("REGISTER_EMAIL"));
            }
            else if ((_g = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _g === void 0 ? void 0 : _g.completed) {
                // External MFA already done, skip captcha and go to ToU
                dispatch(clearNotifications());
                dispatch(signupSlice.actions.setNextPage("SIGNUP_TOU"));
            }
            else {
                dispatch(clearNotifications());
                dispatch(signupSlice.actions.setNextPage("SIGNUP_CAPTCHA"));
            }
        }
        else {
            errors.email = "required";
        }
        return errors;
    }
    return (_jsx(FinalForm, { onSubmit: submitEmailForm, validate: validateSignupUserInForm, initialValues: {
            email: "",
            given_name: (_j = givenName.replace(/^\w/, (c) => c.toUpperCase())) !== null && _j !== void 0 ? _j : "",
            surname: (_k = surname === null || surname === void 0 ? void 0 : surname.replace(/^\w/, (c) => c.toUpperCase())) !== null && _k !== void 0 ? _k : "",
        }, render: (formProps) => {
            var _a, _b, _c, _d;
            const _submitError = Boolean(formProps.submitError && !formProps.dirtySinceLastSubmit);
            const _disabled = Boolean(formProps.hasValidationErrors || _submitError || formProps.pristine);
            return (_jsxs("form", { id: "register-form", onSubmit: formProps.handleSubmit, children: [_jsxs("div", { className: "input-pair", children: [_jsx(FinalField, { component: CustomInput, type: "text", name: "given_name", autoFocus: !((_a = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _a === void 0 ? void 0 : _a.given_name), required: true, placeholder: firstNamePlaceholder, label: _jsx(FormattedMessage, { id: "common.firstName", defaultMessage: "First name", description: "signup first name" }), readOnly: !!((_b = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _b === void 0 ? void 0 : _b.given_name) }), _jsx(FinalField, { component: CustomInput, type: "text", name: "surname", required: true, placeholder: lastNamePlaceholder, label: _jsx(FormattedMessage, { id: "common.lastName", defaultMessage: "Last name", description: "signup last name" }), readOnly: !!((_c = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _c === void 0 ? void 0 : _c.surname) })] }), _jsx(EmailInput, { name: "email", required: true, autoComplete: "username", autoFocus: !!((_d = state === null || state === void 0 ? void 0 : state.external_mfa) === null || _d === void 0 ? void 0 : _d.given_name) }), _jsx("div", { className: "buttons", children: _jsx(EduIDButton, { buttonstyle: "primary", id: "register-button", disabled: _disabled, type: "submit", onClick: formProps.handleSubmit, children: _jsx(FormattedMessage, { id: "common.continue", defaultMessage: "Continue", description: "Signup button" }) }) })] }));
        } }));
}
/**
 * Send the user-provided email address to the backend.
 */
export function RegisterEmail() {
    const email = useAppSelector((state) => state.signup.email);
    const given_name = useAppSelector((state) => state.signup.given_name);
    const surname = useAppSelector((state) => state.signup.surname);
    const signupUser = { email: email !== null && email !== void 0 ? email : "", given_name: given_name !== null && given_name !== void 0 ? given_name : "", surname: surname !== null && surname !== void 0 ? surname : "" };
    const { isSuccess, isError } = signupApi.useRegisterEmailRequestQuery(signupUser);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (isSuccess) {
            dispatch(signupSlice.actions.setNextPage("SIGNUP_ENTER_CODE"));
        }
        else if (isError) {
            dispatch(signupSlice.actions.setNextPage("SIGNUP_ENTRY"));
        }
    }, [isSuccess, isError, dispatch]);
    useEffect(() => {
        // If we don't have the email or name in the state, go back to the email form
        if (!email || !given_name || !surname) {
            dispatch(signupSlice.actions.setNextPage("SIGNUP_ENTRY"));
        }
    }, [email, given_name, surname, dispatch]);
    // Show a blank screen while we wait for the response from the backend
    return null;
}
